import { useContext, useEffect, useState } from "react";
import AntdTable from "../../antd/Table/Table";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useActivateMemberMutation, useDeactivateMemberMutation, useGetAllMembersQuery, useGetAllTeamsQuery } from "../../../Redux/service/Teams/Teams";
import trans, { isRTL } from "../../../utils";
import { Tag } from "antd";
import { popupcontext } from "../../../context/Popupscontext";
import Actions from "../../antd/Table/Actions";
import { toast } from "react-toastify";
import usePermission from "../../../hooks/usePermission";
import moment from "moment";
import TeamMembersHead from "./TeamMembersHead";

const TeamMembers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    systemRole: null,
    accountStatus: "active", // Default to Active
    teamMembership: null,
  });
  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);
  const { handleShow } = useContext(popupcontext);
  const [sort, setSort] = useState({
    by: "",
    order: "",
  });

  // Build query string for filters
  const buildQueryString = (filterObj) => {
    const params = {};
    
    // Map systemRole
    if (filterObj.systemRole) {
      params.systemRole = filterObj.systemRole;
    }
    
    // Map accountStatus: "active" -> disabled=false, "disabled" -> disabled=true
    if (filterObj.accountStatus === "active") {
      params.disabled = "false";
    } else if (filterObj.accountStatus === "disabled") {
      params.disabled = "true";
    }
    
    // Map teamMembership: "has_team" -> hasTeam=true, "no_team" -> hasTeam=false
    if (filterObj.teamMembership === "has_team") {
      params.hasTeam = "true";
    } else if (filterObj.teamMembership === "no_team") {
      params.hasTeam = "false";
    }

    const query = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return query ? `?${query}` : "";
  };

  const queryString = buildQueryString(filter);

  // Fetch all users
  const {
    data: usersData,
    isFetching: isUsersFetching,
    refetch: refetchUsers,
  } = useGetAllMembersQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      sortorder: sort.order,
      queryString: queryString,
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch all teams to get team information
  const { data: teamsData, refetch: refetchTeams } = useGetAllTeamsQuery(
    {
      page: 1,
      limit: 10000,
      search: "",
    },
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const { isAssigningTeam, isAssigningMemberToTeams, isAssigningCompany, edited, setEdited, isEditing, currentMemberId } = useContext(popupcontext);
  const {
    view,
    edit: canEdit,
    delete: canDelete,
  } = usePermission("SNAG");

  const [deactivateUser, { isLoading: isDeactivating }] =
    useDeactivateMemberMutation();
  const [activateUser, { isLoading: isActivating }] = useActivateMemberMutation();

  // Refetch when assign team or assign company popup closes
  useEffect(() => {
    if (!isAssigningTeam && !isAssigningMemberToTeams && !isAssigningCompany) {
      refetchTeams();
      refetchUsers();
    }
  }, [isAssigningTeam, isAssigningMemberToTeams, isAssigningCompany, refetchTeams, refetchUsers]);

  // Refetch users when a team member is created/edited
  useEffect(() => {
    if (edited) {
      refetchUsers();
      setEdited(false);
    }
  }, [edited, refetchUsers, setEdited]);

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filter]);

  useEffect(() => {
    localStorage.setItem("page", currentPage);
  }, [currentPage]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }
  };

  // Get assigned teams for a user
  const getAssignedTeams = (userId) => {
    if (!teamsData?.data) return [];
    return teamsData.data.filter((team) =>
      team.memberUserIds?.includes(userId)
    );
  };

  const handleAssignTeam = (userId) => {
    handleShow("assignTeam", "users", userId);
  };

  const handleDeactivateMember = async (memberId) => {
    try {
      await deactivateUser(memberId).unwrap();
      toast.success(trans("Member deactivated successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
      await refetchUsers();
    } catch (error) {
      console.error("Deactivation error:", error);
      toast.error(
        error?.data?.message || trans("Failed to deactivate member"),
        {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        }
      );
    }
  };

  const handleActivateMember = async (memberId) => {
    try {
      await activateUser(memberId).unwrap();
      toast.success(trans("Member activated successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
      await refetchUsers();
    } catch (error) {
      console.error("Activation error:", error);
      toast.error(error?.data?.message || trans("Failed to activate member"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    }
  };

  const setEditMember = (memberId) => {
    handleShow("editForm", "member", memberId);
  };

  const columns = [
    {
      title: trans("Name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (name) => <span>{name || "-"}</span>,
    },
    {
      title: trans("Email"),
      dataIndex: "email",
      key: "email",
      width: 250,
      render: (email) => (
        <span>{email ? email.replace("@gmail.com", "\n@gmail.com") : "-"}</span>
      ),
    },
    {
      title: trans("Phone Number"),
      dataIndex: "phone",
      key: "phone",
      width: 180,
      render: (phone) => <span>{phone || "-"}</span>,
    },
    {
      title: trans("System Role"),
      key: "systemRole",
      width: 150,
      render: (_, record) => <span>{record.systemRole || "-"}</span>,
    },
    {
      title: trans("Status"),
      key: "disabled",
      width: 150,
      render: (_, record) => <span>{record.disabled ? <Tag color="red">{trans("Disabled")}</Tag> : <Tag color="green">{trans("Active")}</Tag>}</span>,
    },
    {
      title: trans("Last Login"),
      key: "lastLoginAt",
      width: 150,
      render: (_, record) => <span>{record.lastLoginAt ? moment(record.lastLoginAt).format("DD/MM/YYYY HH:mm") : "N/A"}</span>,
    },
    {
      title: trans("Login Count"),
      key: "loginCount",
      width: 150,
      render: (_, record) => <span>{record.loginCount ? record.loginCount : "0"}</span>,
    },
    {
      title: trans("Total Assigned Teams"),
      key: "_count",
      width: 250,
      render: (_, record) => {
        return <span>{record?._count?.teamMemberships ? record._count.teamMemberships : "0"}</span>;
      },
    },
    {
      title: trans("Actions"),
      width: 150,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) => (
        <Actions
          memberId={record.id}
          type="member"
          canEdit={canEdit}
          canDelete={canDelete}
          onDeactivateMember={handleDeactivateMember}
          onActivateMember={handleActivateMember}
          memberStatus={record.disabled}
          setEditMember={setEditMember}
        />
      ),
    },
  ];

  return (
    <>
      <TeamMembersHead setFilter={setFilter} filter={filter} />
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={columns}
          loading={isUsersFetching}
          dataSource={usersData?.data || []}
          pageSize={limit}
          totalItems={usersData?.pagination?.total || 0}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
          scrollX={true}
        />
      </div>
    </>
  );
};

export default TeamMembers;
